import type { GameId } from '@chessmate/protocol';
import { useSyncExternalStore } from 'react';
import type { GameStage } from '../edition/types.js';

/**
 * The operator's remote per-game stages (docs/proposals/remote-game-stages.md), AFTER validation: the edition module checks a payload against
 * this build's games and hands the result to `setRemoteStages`. This module only holds the value and tells subscribers when it changes, so
 * `enabledGames()` can read it synchronously and a screen can re-render when stages arrive. It knows nothing about editions (no import
 * cycle with `edition/index.ts`) and nothing about the network.
 */
export type RemoteStages = Readonly<Partial<Record<GameId, GameStage>>>;

let current: RemoteStages = {};
let version = 0;
const listeners = new Set<() => void>();

export const getRemoteStages = (): RemoteStages => current;

const sameStages = (a: RemoteStages, b: RemoteStages): boolean => {
  const ka = Object.keys(a) as GameId[];
  const kb = Object.keys(b) as GameId[];
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
};

/** Replace the remote stages. Subscribers are told only when something actually changed. */
export function setRemoteStages(next: RemoteStages): void {
  if (sameStages(current, next)) return;
  current = { ...next };
  version += 1;
  for (const l of listeners) l();
}

export function subscribeRemoteStages(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Subscribe a component to the remote stages: it re-renders when they change. Call it wherever `enabledGames()` is read during render. */
export function useRemoteStages(): RemoteStages {
  useSyncExternalStore(subscribeRemoteStages, () => version);
  return current;
}
